import { Controller } from "@hotwired/stimulus"

// Manages the fullscreen teleop console's overlay widgets: show/hide (via the launcher
// buttons in the top bar), minimize (collapse to header), and free drag/resize. State is
// persisted per robot in localStorage so an operator's chosen layout survives reloads.
// This controller is pure DOM/persistence — it knows nothing about the robot. All robot
// behaviour stays in beam_teleop_controller.js, which keeps owning its
// data-beam-teleop-target nodes inside the widget bodies.
//
// Widgets render docked in fixed CSS zones (.teleop-zone--*). A widget only becomes
// free-floating once the operator drags its header or grabs its resize grip: it is then
// reparented into the full-screen widget layer at its current on-screen rect (no visual
// jump) and switched to absolute positioning. Reset re-docks every widget to its zone.
export default class extends Controller {
  static targets = ["widget", "launcher"]
  static values = { robotId: String }

  // Keep a moved widget grabbable: its header must stay below the top bar and inside the
  // viewport. Minimum panel size so a resized widget never collapses to nothing.
  static TOPBAR = 56
  static EDGE = 24
  static MIN_W = 200
  static MIN_H = 120
  static DRAG_THRESHOLD = 3
  // The soundboard widget docks in the full-width bottom bar; the other bottom zones are
  // lifted above it (see syncSoundbar).
  static SOUNDBAR_KEY = "audio"

  connect() {
    this.layer = this.element.querySelector(".teleop-widgets")
    // Remember where each widget lives so Reset can re-dock it in its original order.
    this.widgetTargets.forEach((w) => {
      w._homeZone = w.parentElement
      w._homeIndex = Array.prototype.indexOf.call(w.parentElement.children, w)
    })
    this.restore()
    this.syncLaunchers()
    this._onWindowResize = () => this.clampAll()
    window.addEventListener("resize", this._onWindowResize)
  }

  disconnect() {
    window.removeEventListener("resize", this._onWindowResize)
    this._soundbarObserver?.disconnect()
    this._soundbarObserver = null
    this.unbindPointer()
  }

  get storageKey() {
    return `beam-teleop-layout:${this.robotIdValue}`
  }

  // --- launcher (top-bar) toggles ------------------------------------------------
  toggle(event) {
    const key = event.currentTarget.dataset.widgetKey
    const widget = this.widgetFor(key)
    if (!widget) return
    if (widget.hidden) {
      widget.hidden = false
      widget.classList.remove("is-minimized")
    } else {
      widget.hidden = true
    }
    this.persist()
    this.syncLaunchers()
  }

  // Whole-console fullscreen (distinct from the per-feed fullscreen buttons, which the
  // beam-teleop controller owns).
  fullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      this.element.requestFullscreen?.()
    }
  }

  // --- widget header buttons -----------------------------------------------------
  toggleMinimize(event) {
    const widget = this.widgetFor(event.currentTarget.dataset.widgetKey)
    if (!widget) return
    widget.classList.toggle("is-minimized")
    this.applyHeight(widget)
    this.persist()
  }

  close(event) {
    const widget = this.widgetFor(event.currentTarget.dataset.widgetKey)
    if (!widget) return
    widget.hidden = true
    this.persist()
    this.syncLaunchers()
  }

  // --- drag (header) -------------------------------------------------------------
  dragStart(event) {
    // Let the minimize/close buttons keep working — only a bare header starts a drag.
    if (event.target.closest("button")) return
    const widget = event.currentTarget.closest(".teleop-widget")
    if (!widget) return
    this._active = widget
    this._mode = "drag"
    this._startX = event.clientX
    this._startY = event.clientY
    this._moved = false
    this.bindPointer()
  }

  // --- resize (corner grip) ------------------------------------------------------
  resizeStart(event) {
    event.preventDefault()
    const widget = event.currentTarget.closest(".teleop-widget")
    if (!widget) return
    this.detach(widget)
    const rect = widget.getBoundingClientRect()
    this._active = widget
    this._mode = "resize"
    this._moved = true
    this._startX = event.clientX
    this._startY = event.clientY
    this._origW = rect.width
    this._origH = rect.height
    widget.classList.add("is-dragging")
    this.bindPointer()
  }

  // --- shared pointer lifecycle --------------------------------------------------
  bindPointer() {
    this._moveHandler = this.pointerMove.bind(this)
    this._upHandler = this.pointerUp.bind(this)
    window.addEventListener("pointermove", this._moveHandler)
    window.addEventListener("pointerup", this._upHandler)
    window.addEventListener("pointercancel", this._upHandler)
  }

  unbindPointer() {
    if (!this._moveHandler) return
    window.removeEventListener("pointermove", this._moveHandler)
    window.removeEventListener("pointerup", this._upHandler)
    window.removeEventListener("pointercancel", this._upHandler)
    this._moveHandler = null
    this._upHandler = null
  }

  pointerMove(event) {
    if (!this._active) return
    const dx = event.clientX - this._startX
    const dy = event.clientY - this._startY

    if (this._mode === "drag") {
      // Defer detaching until a real drag begins, so a stray click on the header does
      // not silently convert a docked widget to free-floating.
      if (!this._moved) {
        if (
          Math.abs(dx) < this.constructor.DRAG_THRESHOLD &&
          Math.abs(dy) < this.constructor.DRAG_THRESHOLD
        )
          return
        this._moved = true
        this.detach(this._active)
        this._origLeft = parseFloat(this._active.style.left) || 0
        this._origTop = parseFloat(this._active.style.top) || 0
        this._active.classList.add("is-dragging")
      }
      event.preventDefault()
      const { x, y } = this.clamp(this._active, this._origLeft + dx, this._origTop + dy)
      this._active.style.left = `${x}px`
      this._active.style.top = `${y}px`
    } else {
      event.preventDefault()
      const w = Math.max(this.constructor.MIN_W, this._origW + dx)
      const h = Math.max(this.constructor.MIN_H, this._origH + dy)
      this._active.style.width = `${w}px`
      this._active.style.height = `${h}px`
      this._active._h = h
    }
  }

  pointerUp() {
    const widget = this._active
    const moved = this._moved
    this.unbindPointer()
    this._active = null
    if (!widget) return
    widget.classList.remove("is-dragging")
    if (moved) this.persist()
  }

  // --- free-floating helpers -----------------------------------------------------
  // Lift a docked widget out of its zone into the full-screen widget layer, pinned to
  // its current on-screen position so it does not jump.
  detach(widget) {
    if (widget._free) return
    const rect = widget.getBoundingClientRect()
    const layerRect = this.layer.getBoundingClientRect()
    this.layer.appendChild(widget)
    widget.classList.add("teleop-widget--free")
    widget.style.left = `${rect.left - layerRect.left}px`
    widget.style.top = `${rect.top - layerRect.top}px`
    widget.style.width = `${rect.width}px`
    widget._free = true
  }

  // Keep the header on-screen and below the top bar so a moved widget stays grabbable.
  clamp(widget, x, y) {
    const layerRect = this.layer.getBoundingClientRect()
    const maxX = layerRect.width - this.constructor.EDGE
    const maxY = layerRect.height - this.constructor.EDGE
    const minX = this.constructor.EDGE - widget.offsetWidth
    return {
      x: Math.min(Math.max(x, minX), maxX),
      y: Math.min(Math.max(y, this.constructor.TOPBAR), maxY),
    }
  }

  clampAll() {
    this.widgetTargets.forEach((w) => {
      if (!w._free) return
      const { x, y } = this.clamp(w, parseFloat(w.style.left) || 0, parseFloat(w.style.top) || 0)
      w.style.left = `${x}px`
      w.style.top = `${y}px`
    })
  }

  // When minimized a free widget collapses to its header; otherwise it keeps the
  // operator's chosen height (with the body scrolling if content overflows).
  applyHeight(widget) {
    if (!widget._free) return
    if (widget.classList.contains("is-minimized") || !widget._h) {
      widget.style.height = ""
    } else {
      widget.style.height = `${widget._h}px`
    }
  }

  resetLayout() {
    const affected = new Set()
    this.widgetTargets.forEach((w) => {
      if (!w._free) return
      w.classList.remove("teleop-widget--free", "is-dragging")
      w.style.left = w.style.top = w.style.width = w.style.height = ""
      w._free = false
      w._h = null
      affected.add(w._homeZone)
    })
    // Re-dock in original order: appending every widget of an affected zone in ascending
    // home index restores the per-zone stacking regardless of detach order.
    this.widgetTargets
      .slice()
      .sort((a, b) => a._homeIndex - b._homeIndex)
      .forEach((w) => {
        if (affected.has(w._homeZone)) w._homeZone.appendChild(w)
      })
    this.persist()
    this.syncLaunchers()
    // The stage controller (separate element, so no direct data-action) re-docks its
    // floating video tiles on the same reset.
    window.dispatchEvent(new CustomEvent("beam-teleop:reset-layout"))
  }

  // --- helpers -------------------------------------------------------------------
  widgetFor(key) {
    return this.widgetTargets.find((w) => w.dataset.widgetKey === key)
  }

  // Reflect each widget's open state on its launcher button so the operator can see
  // at a glance what is showing.
  syncLaunchers() {
    if (!this.hasLauncherTarget) return
    this.launcherTargets.forEach((btn) => {
      const widget = this.widgetFor(btn.dataset.widgetKey)
      const open = widget && !widget.hidden
      btn.classList.toggle("is-active", Boolean(open))
      btn.setAttribute("aria-pressed", open ? "true" : "false")
    })
    this.syncSoundbar()
  }

  // The soundboard bar spans the bottom of the console, so publish its height as a CSS
  // custom property and let the other bottom zones (PiP rail, drive sticks, commands) sit
  // above it instead of being covered. The height is measured rather than hard-coded: the
  // bar wraps to more rows the more clips a robot has. A bar the operator dragged out of
  // its zone is free-floating and pushes nothing.
  syncSoundbar() {
    const widget = this.widgetFor(this.constructor.SOUNDBAR_KEY)
    const docked = widget && !widget.hidden && !widget._free
    this.element.style.setProperty("--teleop-soundbar-h", `${docked ? widget.offsetHeight : 0}px`)
    if (widget && !this._soundbarObserver && window.ResizeObserver) {
      this._soundbarObserver = new ResizeObserver(() => this.syncSoundbar())
      this._soundbarObserver.observe(widget)
    }
  }

  persist() {
    const state = {}
    this.widgetTargets.forEach((w) => {
      const entry = {
        open: !w.hidden,
        minimized: w.classList.contains("is-minimized"),
      }
      if (w._free) {
        entry.free = true
        entry.x = parseFloat(w.style.left) || 0
        entry.y = parseFloat(w.style.top) || 0
        entry.w = w.offsetWidth
        if (w._h) entry.h = w._h
      }
      state[w.dataset.widgetKey] = entry
    })
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state))
    } catch {
      /* private mode / quota — layout simply won't persist */
    }
    // Minimizing or dragging the soundboard bar changes what the bottom zones must clear.
    this.syncSoundbar()
  }

  restore() {
    let state
    try {
      state = JSON.parse(localStorage.getItem(this.storageKey) || "null")
    } catch {
      state = null
    }
    if (!state) return
    this.widgetTargets.forEach((w) => {
      const saved = state[w.dataset.widgetKey]
      if (!saved) return
      w.hidden = !saved.open
      // A frameless widget (no header) has no minimize button and no drag handle, so a
      // stale minimized/free state would collapse or strand it with no way back — only
      // its open flag may be restored.
      if (!w.querySelector(".teleop-widget__head")) return
      w.classList.toggle("is-minimized", Boolean(saved.minimized))
      if (!saved.free) return
      this.detach(w)
      const { x, y } = this.clamp(w, saved.x || 0, saved.y || 0)
      w.style.left = `${x}px`
      w.style.top = `${y}px`
      if (saved.w) w.style.width = `${saved.w}px`
      if (saved.h) w._h = saved.h
      this.applyHeight(w)
    })
  }
}
